import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { ModelId } from '../providers/types';

export type UiStatus = 'idle' | 'listening' | 'speaking' | 'inferring';

export interface AssistantTurn {
  id: string;
  role: 'model';
  text: string;
  model: ModelId;
  first_token_ms?: number;
  total_ms?: number;
  created_at: string;
}

export interface UserEvent {
  id: string;
  segmentId: string;
  durationSec: number;
  created_at: string;
}

export interface ConversationState {
  turns: AssistantTurn[];
  userEvents: UserEvent[];
  status: UiStatus;
  activeAssistantId: string | null;

  setStatus: (s: UiStatus) => void;
  recordUser: (args: { segmentId: string; durationSec: number }) => void;
  beginAssistant: (model: ModelId) => string;
  appendTokens: (text: string) => void;
  finalizeAssistant: (metrics?: { first_token_ms?: number; total_ms?: number }) => void;
  // model defaults to gemini-2.5-flash for test convenience; production
  // callers should always pass the actual model used for the turn.
  appendAssistant: (text: string, model?: ModelId) => void;
  reset: () => void;
}

export interface ConversationStoreDeps {
  storage: Storage;
  key: string;
  maxTurns?: number;
}

function newId(prefix = 't'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createConversationStore(
  deps: ConversationStoreDeps,
): UseBoundStore<StoreApi<ConversationState>> {
  const maxTurns = deps.maxTurns ?? 5;
  const persisted = (() => {
    try {
      const raw = deps.storage.getItem(deps.key);
      return raw ? (JSON.parse(raw) as { turns: AssistantTurn[]; userEvents: UserEvent[] }) : null;
    } catch {
      return null;
    }
  })();

  const persist = (s: Pick<ConversationState, 'turns' | 'userEvents'>) => {
    try {
      deps.storage.setItem(deps.key, JSON.stringify({ turns: s.turns, userEvents: s.userEvents }));
    } catch {
      // ignore quota
    }
  };

  return create<ConversationState>((set, get) => ({
    turns: persisted?.turns ?? [],
    userEvents: persisted?.userEvents ?? [],
    status: 'idle',
    activeAssistantId: null,

    setStatus(s) {
      set({ status: s });
    },

    recordUser({ segmentId, durationSec }) {
      const ev: UserEvent = {
        id: newId('u'),
        segmentId,
        durationSec,
        created_at: new Date().toISOString(),
      };
      const next = [...get().userEvents, ev].slice(-maxTurns * 2);
      set({ userEvents: next });
      persist({ ...get(), userEvents: next });
    },

    beginAssistant(model) {
      const id = newId('a');
      const turn: AssistantTurn = {
        id,
        role: 'model',
        text: '',
        model,
        created_at: new Date().toISOString(),
      };
      const next = [...get().turns, turn].slice(-maxTurns);
      set({ turns: next, activeAssistantId: id });
      persist({ ...get(), turns: next });
      return id;
    },

    appendTokens(text) {
      const { activeAssistantId, turns } = get();
      if (!activeAssistantId) return;
      const next = turns.map((t) =>
        t.id === activeAssistantId ? { ...t, text: t.text + text } : t,
      );
      // Skip persist during streaming: calling localStorage.setItem once per
      // token is expensive on the main thread. finalizeAssistant() writes
      // the final text. If the tab is closed mid-stream, the turn is lost —
      // acceptable because it was incomplete anyway.
      set({ turns: next });
    },

    finalizeAssistant(metrics) {
      const { activeAssistantId, turns } = get();
      if (!activeAssistantId) return;
      const next = turns.map((t) =>
        t.id === activeAssistantId
          ? { ...t, first_token_ms: metrics?.first_token_ms, total_ms: metrics?.total_ms }
          : t,
      );
      set({ turns: next, activeAssistantId: null });
      persist({ ...get(), turns: next });
    },

    appendAssistant(text, model = 'gemini-2.5-flash') {
      const id = newId('a');
      const turn: AssistantTurn = {
        id,
        role: 'model',
        text,
        model,
        created_at: new Date().toISOString(),
      };
      const next = [...get().turns, turn].slice(-maxTurns);
      set({ turns: next, activeAssistantId: null });
      persist({ ...get(), turns: next });
    },

    reset() {
      set({ turns: [], userEvents: [], activeAssistantId: null, status: 'idle' });
      persist({ ...get(), turns: [], userEvents: [] });
    },
  }));
}
